import { getSubmitInspectionMutationOptions } from 'api-client';
import {
  MutationOptions as RegistryMutationOptions,
  defaultMutationOptions,
} from 'common/infrastructure/remoteState/mutations.interface';
import {
  markSubmissionHostDone,
  failWorkSubmissionsForHost,
} from 'common/infrastructure/work-task/workSubmissionOutbox';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { captureEvent } from 'common/infrastructure/cookies/usePostHogEvents';
import {
  takeInspectionSubmitContext,
  forgetInspectionSubmitContext,
} from './inspectionSubmitAnalytics';
import { EVENT_INSPECTION_SUBMIT_FAILED } from 'common/infrastructure/analytics/events';

/**
 * Offline-capable submit-inspection mutation config.
 *
 * Registered globally (remoteState/mutations.ts) so a submit performed while
 * offline is paused and resumed via resumePausedMutations. Uses the same
 * `networkMode: 'online'` as the photo upload mutation; because TanStack resumes
 * paused mutations in FIFO order, the photo uploads (queued first) run before
 * the submit that references their IDs.
 *
 * The shared `mutationKey` is `['submitInspection']`, which matches the orval
 * `useSubmitInspection` hook, so the hook automatically picks up these defaults
 * (networkMode + retry + error logging) via setMutationDefaults.
 */
export function getSubmitInspectionMutationConfig(): RegistryMutationOptions {
  return getSubmitInspectionMutationOptions({
    mutation: {
      ...defaultMutationOptions,
      // The inspectionId (client-minted, carried in the persisted variables) doubles as
      // the work-outbox submissionId, so this success — even on resume after a restart —
      // unblocks any work queued against this inspection. Harmless no-op when there's none.
      onSuccess: (_data: unknown, variables: { inspectionId?: string }) => {
        if (variables?.inspectionId) {
          markSubmissionHostDone(variables.inspectionId, {});
          forgetInspectionSubmitContext(variables.inspectionId);
        }
      },
      onError: (error: unknown, variables: { inspectionId?: string }) => {
        loggerService.log(LoggerLevel.ERROR, 'Submit inspection error', error);
        if (variables?.inspectionId) {
          failWorkSubmissionsForHost(
            variables.inspectionId,
            error instanceof Error ? error.message : String(error)
          );
          // Tracked here rather than from the form hook: the optimistic paths
          // navigate away immediately, and TanStack skips a per-call onError once
          // the observer has no listeners, so the failure would go unrecorded.
          // This handler also runs on offline resume after a restart.
          const context = takeInspectionSubmitContext(variables.inspectionId);
          if (context) {
            captureEvent(EVENT_INSPECTION_SUBMIT_FAILED, {
              ...context,
              error_type: error instanceof Error ? error.name : 'Unknown',
              error_message: error instanceof Error ? error.message : 'Unknown error',
            });
          }
        }
      },
    },
  }) as unknown as RegistryMutationOptions;
}
